import React from 'react'
import { generatePath, Link, NavLink } from 'react-router-dom'

import { AddOutline } from 'react-ionicons'
import { Button, FlexboxGrid, Nav, Navbar, Panel, Table } from 'rsuite'

import SettingsTopMenu from '@components/settings/SettingsTopMenu'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'

import type { Menu } from '@@partner:types:app'

import { 
  ROUTE_SETTINGS_MENUS_CREATE,
  ROUTE_SETTINGS_MENUS_EDIT
} from '@@partner:config/routes'

import { useFetchAllMenus } from '@@partner:graphql/query/menu/allMenus.gql'

const { Column, HeaderCell, Cell } = Table

export default function MenusPage () {
  const { data, loading, error } = useFetchAllMenus({
    pager: { 
      page: 1, 
      limit: 100 
    },
    sort: {
      fieldName: 'created_at',
      direction: -1
    }
  })
  
  const menus = data?.allMenus?.edges ?? []

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title="Ошибка загрузки меню"
        error={error}
      />
    )
  }

  return (
    <div className="MenusPage">
      <h1 className="u-heading">Управление меню</h1>
      <SettingsTopMenu />
      
      <Navbar appearance='subtle'>
        <Nav>
          <Nav.Item
            icon={<AddOutline />}
            as={NavLink}
            to={ROUTE_SETTINGS_MENUS_CREATE}
          >
            Создать меню
          </Nav.Item>
        </Nav>
      </Navbar>

      <Panel shaded>
        <Table
          loading={loading}
          data={menus}
          autoHeight
          rowHeight={60}
        >
          <Column width={80} align="center" fixed>
            <HeaderCell>ID</HeaderCell>
            <Cell dataKey="id" />
          </Column>

          <Column width={200} flexGrow={1}>
            <HeaderCell>Название</HeaderCell>
            <Cell dataKey="name" />
          </Column>

          <Column width={150}>
            <HeaderCell>Код</HeaderCell>
            <Cell>
              {(rowData: Menu) => (
                <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                  {rowData.code}
                </code>
              )}
            </Cell>
          </Column>

          <Column width={120}>
            <HeaderCell>Тип</HeaderCell>
            <Cell dataKey="type" />
          </Column>

          <Column width={100} align="center">
            <HeaderCell>Статус</HeaderCell>
            <Cell>
              {(rowData: Menu) => (
                <span className={`px-2 py-1 rounded text-sm ${
                  rowData.is_active 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {rowData.is_active ? 'Активно' : 'Неактивно'}
                </span>
              )}
            </Cell>
          </Column>

          <Column width={100} align="center">
            <HeaderCell>Элементов</HeaderCell>
            <Cell>
              {(rowData: Menu) => (
                <Link 
                  to={generatePath(ROUTE_SETTINGS_MENUS_EDIT, { 
                    menuId: rowData.id 
                  })}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {rowData.menuItemsCount || 0}
                </Link>
              )}
            </Cell>
          </Column>

          <Column width={180} align="center">
            <HeaderCell>Создано</HeaderCell>
            <Cell>
              {(rowData: Menu) => (
                new Date(rowData.created_at).toLocaleDateString('ru-RU')
              )}
            </Cell>
          </Column>

          <Column width={150} align="center" fixed="right">
            <HeaderCell>Действия</HeaderCell>
            <Cell>
              {(rowData: Menu) => (
                <FlexboxGrid justify="center" align="middle">
                  <Button
                    size="xs"
                    appearance="link"
                    as={Link}
                    to={generatePath(ROUTE_SETTINGS_MENUS_EDIT, { 
                      menuId: rowData.id 
                    })}
                  >
                    Редактировать
                  </Button>
                </FlexboxGrid>
              )}
            </Cell>
          </Column>
        </Table>
      </Panel>
    </div>
  )
}
